import { Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { componentsTestIds } from '@components/testids';
// The severity vocabulary is GRAMMAR (`UiWidgetAlertSchema`'s enum), so it lives in
// `readmodelSource.ts` with the rest of it — one definition shared by this renderer,
// the inspector's option list and `validateAlert`. See that file.
import { isAlertSeverity, type AlertSeverity } from '../readmodelSource';
import { formatKpiValue, sanitizeKpiHref } from './kpiData';
import { resolveStepColor, type ColorStep } from './colorSteps';
import { readmodelDisplayCount, type ReadmodelWidgetState } from './readmodelState';
import { ReadmodelEmptyHint, ReadmodelLoading, ReadmodelUnavailable } from './ReadmodelWidgetStates';
import type { CSSProperties } from 'react';
import { WidgetLink } from './WidgetLink';
import classes from './AlertWidget.module.css';

/**
 * TV1-2291 (SDD §5/§6 · t25) — the `alert` tile: the four `AlertsSection` tiles,
 * expressed as widgets.
 *
 * A `count` plus two things a count does not have: a semantic SEVERITY, and a deep
 * link to go act on the number. `DashboardIndicator` — the hand-written component
 * this replaces — renders exactly those, plus a title the card chrome now owns.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * 🔴 C5 — `href` IS A STORED-XSS SINK. READ THIS BEFORE TOUCHING THE ANCHOR.
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * `UiWidgetAlertSchema` declares `widgetComponentProps.href` as `S.string()`, and
 * **that is not a security control**: a `javascript:` URL validates cleanly at the
 * schema layer, and there is a deliberate negative test in
 * `uiWidgetsLayout.widening.test.ts` proving it.
 *
 * Widget layouts are **SHARED account-scoped documents** — one user authors a
 * report, every other user in the account reads it. That makes `href` a PERSISTED,
 * CROSS-USER sink: the same field class TV1-1934 was a stored XSS through, on
 * `kpi.href`, by exactly this route (an authored `javascript:` URI falling through
 * to a raw `<a href={href}>`).
 *
 * Three rules, all load-bearing:
 *
 *  1. **The existing sanitiser, not a new one.** `sanitizeKpiHref` (`kpiData.ts`) is
 *     the tested one — scheme ALLOW-list (in-app `/…`, `http:`, `https:`), parsed
 *     rather than substring-matched, rejecting C0 controls (`java\tscript:`),
 *     backslashes (`/\evil.com`) and protocol-relative (`//evil.com`). A second
 *     sanitiser would drift from it, and the drift would be silent. Its name still
 *     says "Kpi" because renaming a 69-test module across the tree buys nothing;
 *     it is the app's ONE widget-href sanitiser, and `kpiState.parseKpiPrerequisite`
 *     already reuses it under that name.
 *  2. **FAIL CLOSED.** `sanitizeKpiHref` returns `null` for anything off the list,
 *     and a `null` target renders the number as PLAIN TEXT WITH NO ANCHOR. Never a
 *     dead `<a>`, never `href="#"`, never a raw fallback: a link that silently
 *     points somewhere other than where its author meant is worse than no link.
 *  3. **DO NOT "fix" this by tightening the schema.** A URL allow-list in jsonschema
 *     would be a second, weaker sanitiser that drifts from this one, AND it would
 *     reject at SAVE time a layout the SERVER accepts — breaking the FE/server ajv
 *     parity that ADR-061 guarantees by construction. The renderer is the correct
 *     boundary, because the renderer is where a string becomes a navigation.
 *
 * Pinned by `AlertWidget.test.tsx`, which asserts a `javascript:` href and a `data:`
 * href each render zero anchors.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * ## The severity is drawn even at zero — deliberately, and this was a call
 *
 * `DashboardIndicator` paints its `alertLevel` regardless of the value, so a tile
 * reading "0 quiebres de stock" is still the danger tile today. Reproducing that is
 * this ticket's AC ("alert reproduces an AlertsSection tile"), so the treatment
 * stays constant and the widget does NOT downgrade its own severity at zero.
 *
 * The alternative — calm the tile when there is nothing to act on — is a real design
 * improvement and was NOT taken here, because it changes what a colour MEANS in a
 * shared document and that is an owner decision, not an implementation one. What
 * ships instead is honest and cheap: `data-alert-resolved` plus the inline "nothing
 * matches" hint, so a reader can tell a red zero from a red twelve without the
 * palette having to change meaning.
 */

const SEVERITY_CLASS: Record<AlertSeverity, string> = {
  low: classes.severityLow,
  medium: classes.severityMedium,
  high: classes.severityHigh
};


export type AlertWidgetProps = {
  /** The resolved state — see `readmodelState.resolveReadmodelWidgetState`. */
  state: ReadmodelWidgetState;
  /**
   * `UiWidgetAlertSchema`'s REQUIRED enum. Defaulted to `'medium'` at the RENDERER
   * only: the schema refuses to persist a widget without one and the inspector
   * refuses to leave it unset, so this default is reached only by a malformed
   * fixture or a hand-edited layout. `'medium'` is the middle claim — a defaulted
   * `'high'` would assert an urgency nobody authored.
   */
  severity?: AlertSeverity;
  /** 🔴 UNTRUSTED. Allow-listed by `sanitizeKpiHref` — see the C5 block above. */
  href?: string;
  /**
   * 🔗 THE LINK GATE (2026-08-19). `true` renders the number as PLAIN TEXT even when
   * `href` is present and valid.
   *
   * Review item 6: *"on edit mode, widgets with linked routes shouldnt navigte you,
   * only on view mode"*. In the editor a card is an OBJECT you are arranging — you
   * click it to select it, drag it, resize it — and a click that navigates away
   * mid-edit loses the position of everything you have not saved. The link is the
   * READER's affordance; the author's click means "select this".
   *
   * Suppressed at the renderer rather than by clearing `href`: the value must stay
   * on the widget so the inspector can show and edit it.
   */
  editMode?: boolean;
  /**
   * OPTIONAL step colouring, already validated by `parseColorSteps` (2026-08-18).
   *
   * When a band matches the count it REPLACES the severity's colour — see the
   * header note on why that is the right precedence.
   */
  colorSteps?: ColorStep[];
  /** Optional label under the number (the card chrome already renders the title). */
  caption?: string;
  /** Explicit locale for deterministic tests; defaults to the runtime locale. */
  locale?: string;
};

export const AlertWidget = ({ state, severity = 'medium', href, colorSteps, caption, editMode = false, locale }: AlertWidgetProps ) => {
  const { t } = useTranslation( 'widgets' );

  if ( state.kind === 'loading' ) return <ReadmodelLoading />;
  // `error` is normally intercepted by `WebWidget`'s error tile before a parser runs.
  // Handled anyway so a failed fetch can never surface as a confident alert count.
  if ( state.kind === 'error' || state.kind === 'unavailable' ) return <ReadmodelUnavailable />;

  const count = readmodelDisplayCount( state );
  const resolvedSeverity = isAlertSeverity( severity ) ? severity : 'medium';

  /*
   * A matching band OVERRIDES the severity, and the precedence is the whole point.
   *
   * Severity is what the author asserted about the MEASURE ("stock breaks are
   * serious"); a step is what they asserted about the READING ("...but under ten of
   * them is routine"). The second is strictly more specific, so it wins. Without
   * step colours nothing changes — `resolveStepColor` returns `undefined` and the
   * severity class supplies `--alert-color` exactly as before.
   *
   * Below the lowest band the step list says nothing (see `resolveStepColor`), so a
   * count under the first floor falls back to the severity rather than to the first
   * band's colour — an author who starts at 10 has made no claim about 3.
   */
  const stepColor = resolveStepColor( count, colorSteps );
  const stepStyle: CSSProperties | undefined = stepColor
    ? ({ '--alert-color': stepColor } as CSSProperties )
    : undefined;

  // 🔴 C5. `sanitizeKpiHref` returns `null` for anything that is not an in-app path
  // or an http(s) URL, and `null` renders PLAIN TEXT WITH NO ANCHOR below. There is
  // deliberately no `else` branch that reaches `href` again.
  // 🔗 `editMode` short-circuits BEFORE the sanitiser, so an author's click can
  // never navigate regardless of how good the href is.
  const linkTarget = editMode ? null : sanitizeKpiHref( href );

  /*
   * `DashboardIndicator`'s layout, not the prototype's (2026-08-19, review item 5:
   * alerts follow the Dashboard's fixed tiles). There the TITLE sits top-left and the
   * ICON top-right, with the big number on its own line below. The card chrome
   * already renders the title in that slot, so the badge is positioned into the
   * top-right corner of the BODY and the number gets the full width under it —
   * which reproduces the dashboard tile exactly, with the title supplied by the card.
   *
   * It was previously an inline row (badge then number, side by side), which is the
   * prototype's `.alertbox` and reads as a different component family from the
   * dashboard tiles sitting directly below it on the same page.
   */
  const body = (
    <div className={classes.row}>
      <Text
        component="div"
        className={classes.value}
        data-testid={componentsTestIds.alertWidgetValue}
        title={state.kind === 'empty'
          ? t( 'widget_readmodel_empty_hint', 'Nothing matches right now' )
          : undefined}
      >
        {/* `decimals: 0` — a row count is an integer by construction. */}
        {formatKpiValue( count, 'number', { locale, decimals: 0 })}
      </Text>
    </div>
  );

  /*
   * ⚠️ THE WHOLE TILE IS THE LINK, not just the number (2026-08-19), on the ticket
   * owner's call. An alert exists to be acted on — making the caption and the
   * "nothing matches" hint part of the same target is what makes the card behave
   * like the one object it looks like.
   */
  return (
    <WidgetLink
      target={linkTarget}
      testId={componentsTestIds.alertWidgetLink}
      className={classes.link}
    >
    <div
      /*
       * The severity class is on the CONTAINER, not on the badge (2026-08-18). It
       * sets one custom property, `--alert-color`, which both the icon square and
       * the number read — so a step colour overrides both with a single inline
       * declaration instead of the renderer having to recolour each element.
       */
      className={`${classes.container} ${SEVERITY_CLASS[resolvedSeverity]}`}
      style={stepStyle}
      data-testid={componentsTestIds.alertWidget}
      data-edit-mode={editMode ? 'true' : undefined}
      data-readmodel-state={state.kind}
      data-severity={resolvedSeverity}
      // The band that actually painted, for tests and for anyone reading the DOM to
      // work out why a tile is the colour it is. Absent when no band matched.
      data-step-color={stepColor}
      // "There is nothing to act on right now" WITHOUT changing what the severity
      // colour means — see the header note on the call that was not taken.
      data-alert-resolved={state.kind === 'empty' ? 'true' : undefined}
    >
      {body}

      {/*
        ⚠️ THE BADGE IS NOT HERE ANY MORE (2026-08-19). It is rendered by
        `WidgetCard`, on the same row as the title.

        It used to be positioned against THIS box, which sits inside `.cardBody` —
        below the title — so it could never rise higher than the top of the body and
        always sat a title's height too low. The ticket owner asked for the
        `DashboardIndicator` alignment (title left, icon right, one row), and the
        only element that can produce it is the one that draws the title.

        `collection` is still read here for nothing else, so it stayed a prop rather
        than being deleted: the card resolves its own icon from the definition.
      */}
      {state.kind === 'empty' ? <ReadmodelEmptyHint /> : null}

      {/*
        ⚠️ THE CAPTION IS SUPPRESSED IN AN EMPTY STATE (2026-08-19, review item 2:
        "on empty states, you shouldnt show anything but the title and empty state").

        An empty tile was showing three things at once — the measured `0`, the
        "nothing matches right now" hint, and the author's caption — which is more
        text than a populated tile carries, for a card that has nothing to say. The
        hint IS the empty state and it stays; the caption is context for a reading
        that is not there.
      */}
      {caption && state.kind === 'ready' ? (
        <Text component="div" className={classes.caption} data-testid={componentsTestIds.alertWidgetCaption}>
          {caption}
        </Text>
      ) : null}
    </div>
    </WidgetLink>
  );
};
